import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import DateFieldInput from "../DateFieldInput";

function todayIso() {
  return new Date().toISOString().split("T")[0];
}

function nowTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const EMPTY_USCITA = {
  data_uscita: todayIso(),
  ora_uscita: nowTime(),
  motivo: "",
};

const EMPTY_RIENTRO = {
  data_rientro: todayIso(),
  ora_rientro: nowTime(),
};

export function UscitaTemporaneaDialog({
  open,
  onOpenChange,
  initialData,
  onSubmit,
  saving = false,
}) {
  const [form, setForm] = useState(EMPTY_USCITA);

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setForm({
        data_uscita: initialData.data_uscita || todayIso(),
        ora_uscita: initialData.ora_uscita || nowTime(),
        motivo: initialData.motivo || "",
      });
    } else {
      setForm({ ...EMPTY_USCITA, data_uscita: todayIso(), ora_uscita: nowTime() });
    }
  }, [open, initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      data_uscita: form.data_uscita,
      ora_uscita: form.ora_uscita,
      motivo: form.motivo.trim(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{initialData ? "Modifica uscita temporanea" : "Nuova uscita temporanea"}</DialogTitle>
          <DialogDescription>
            Indica data, ora e motivo dell&apos;uscita temporanea dell&apos;ospite.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data uscita</Label>
              <DateFieldInput
                value={form.data_uscita}
                onChange={(v) => setForm((prev) => ({ ...prev, data_uscita: v }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Ora uscita</Label>
              <Input
                type="time"
                value={form.ora_uscita}
                onChange={(e) => setForm((prev) => ({ ...prev, ora_uscita: e.target.value }))}
                className="mt-1"
                required
              />
            </div>
          </div>
          <div>
            <Label>Motivo</Label>
            <Textarea
              value={form.motivo}
              onChange={(e) => setForm((prev) => ({ ...prev, motivo: e.target.value }))}
              placeholder="Es. Visita medica, permesso familiare..."
              rows={3}
              className="mt-1"
              required
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={saving || !form.motivo.trim()}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {initialData ? "Salva modifiche" : "Registra uscita"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RientroUscitaDialog({
  open,
  onOpenChange,
  initialData,
  onSubmit,
  saving = false,
}) {
  const [form, setForm] = useState(EMPTY_RIENTRO);

  useEffect(() => {
    if (!open) return;
    setForm({
      data_rientro: initialData?.data_rientro || todayIso(),
      ora_rientro: initialData?.ora_rientro || nowTime(),
    });
  }, [open, initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      data_rientro: form.data_rientro,
      ora_rientro: form.ora_rientro,
    });
  };

  const isEdit = Boolean(initialData?.data_rientro);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica rientro" : "Registra rientro"}</DialogTitle>
          <DialogDescription>
            {initialData?.motivo ? (
              <>
                Uscita: <span className="font-medium">{initialData.motivo}</span>
              </>
            ) : (
              "Indica data e ora di rientro in struttura."
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data rientro</Label>
              <DateFieldInput
                value={form.data_rientro}
                onChange={(v) => setForm((prev) => ({ ...prev, data_rientro: v }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Ora rientro</Label>
              <Input
                type="time"
                value={form.ora_rientro}
                onChange={(e) => setForm((prev) => ({ ...prev, ora_rientro: e.target.value }))}
                className="mt-1"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {isEdit ? "Salva rientro" : "Conferma rientro"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
